import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface HeaderConfig {
  logo_url: string;
  show_search: boolean;
  show_theme_toggle: boolean;
  fixed_position: boolean;
  background_color: string;
  background_dark_color: string;
  text_color: string;
  text_dark_color: string;
  transparency_level: number;
}

interface SiteConfig {
  site_name: string;
  site_subtitle: string;
  logo_url: string;
}

interface NavigationItem {
  id: string;
  label: string;
  url: string;
  icon?: string;
  is_dropdown: boolean;
  is_external: boolean;
  parent_id?: string | null;
  order: number;
}

interface NavigationSubItem {
  id: string;
  label: string;
  url: string;
  parent_id: string;
  is_external: boolean;
}

interface DocumentCategory {
  name: string;
  slug: string;
  icon: string;
  show_in_header: boolean;
  subcategories: {
    name: string;
    slug: string;
  }[];
}

export function useHeaderConfig() {
  const [headerConfig, setHeaderConfig] = useState<HeaderConfig | null>(null);
  const [navItems, setNavItems] = useState<NavigationItem[]>([]);
  const [navSubItems, setNavSubItems] = useState<NavigationSubItem[]>([]);
  const [siteConfig, setSiteConfig] = useState<SiteConfig>({
    site_name: "PREFEITURA MUNICIPAL",
    site_subtitle: "MUNICÍPIO DE INTERESSE TURÍSTICO",
    logo_url: ""
  });
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    loadCategories();
    loadHeaderConfig();
    loadSiteConfig();
  }, []);

  const loadSiteConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('site_configs')
        .select('site_name, site_subtitle, logo_url')
        .single();

      if (error) throw error;
      
      if (data) {
        setSiteConfig({
          site_name: data.site_name || "PREFEITURA MUNICIPAL",
          site_subtitle: data.site_subtitle || "MUNICÍPIO DO ESTADO DE SÃO PAULO",
          logo_url: data.logo_url || ""
        });
      }
    } catch (error) {
      console.error('Error loading site config:', error);
    }
  };

  const loadHeaderConfig = async () => {
    try {
      setConfigLoading(true);
      // Load header configuration
      const { data: configData, error: configError } = await supabase
        .from('header_configs')
        .select('*')
        .single();

      if (configError) throw configError;
      
      setHeaderConfig(configData || getDefaultHeaderConfig());

      // Load navigation items
      const { data: navItemsData, error: navItemsError } = await supabase
        .from('navigation_items')
        .select('*')
        .eq('type', 'header')
        .eq('active', true)
        .order('order', { ascending: true });

      if (navItemsError) throw navItemsError;
      
      setNavItems(navItemsData || []);

      // Load navigation subitems
      const { data: subItemsData, error: subItemsError } = await supabase
        .from('navigation_subitems')
        .select('*')
        .eq('active', true)
        .order('order', { ascending: true });

      if (subItemsError) throw subItemsError;
      
      setNavSubItems(subItemsData || []);
    } catch (error) {
      console.error('Error loading header configuration:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const getDefaultHeaderConfig = (): HeaderConfig => {
    return {
      logo_url: "https://erosis.com.br/uploads/sp.png",
      show_search: true,
      show_theme_toggle: true,
      fixed_position: true,
      background_color: "#ffffff",
      background_dark_color: "#0f172a",
      text_color: "#1e293b",
      text_dark_color: "#f1f5f9",
      transparency_level: 100,
    };
  };

  const loadCategories = async () => {
    try {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('document_categories')
        .select('*')
        .eq('active', true)
        .eq('show_in_header', true)
        .order('order');

      if (categoriesError) throw categoriesError;

      const categoriesWithSubs = await Promise.all(
        (categoriesData || []).map(async (category) => {
          const { data: subcategories, error: subError } = await supabase
            .from('document_subcategories')
            .select('*')
            .eq('category_id', category.id)
            .eq('active', true)
            .eq('show_in_header', true)
            .order('order');

          if (subError) throw subError;

          return {
            name: category.name,
            slug: category.slug,
            icon: category.icon,
            subcategories: (subcategories || []).map((sub) => ({
              name: sub.name_plural,
              slug: sub.slug,
            })),
          };
        })
      );

      setCategories(categoriesWithSubs);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    headerConfig,
    navItems,
    navSubItems,
    siteConfig,
    categories,
    loading,
    configLoading
  };
}